import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, ArrowLeft, Tag, User, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import { useLanguage } from "@/hooks/useLanguage";
import { getBlogPostBySlug, BlogPost } from "@/lib/blog-service";

const DynamicBlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setError("Post not found");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getBlogPostBySlug(slug);
        if (data) {
          setPost(data);
        } else {
          setError("Post not found");
        }
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError("Error loading post");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Simple markdown to HTML conversion for basic formatting with XSS protection
  const renderContent = (content: string) => {
    // Process markdown-like formatting
    let html = content
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-3">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-8 mb-4">$1</h1>')
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/g, '<strong class="font-bold italic">$1</strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>')
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mb-4">')
      .replace(/\n/g, '<br />');
    
    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(`<p class="mb-4">${html}</p>`, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'class', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="py-40 text-center">
          <h1 className="text-3xl font-bold mb-4">
            {language === 'es' ? 'Artículo no encontrado' : 'Post Not Found'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {language === 'es' 
              ? 'El artículo que buscas no existe o ha sido eliminado.'
              : 'The article you are looking for does not exist or has been removed.'}
          </p>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Volver al Blog' : 'Back to Blog'}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const publishedDate = post.published_at 
    ? new Date(post.published_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
        year: "numeric", 
        month: "long", 
        day: "numeric" 
      })
    : null;

  return (
    <div className="min-h-screen">
      <SEO 
        title={`${post.title} | Daniel Albert Law Firm`}
        description={post.excerpt || post.content.substring(0, 160)}
        keywords={post.tags?.join(', ') || post.category || ''}
      />
      <Header />

      {/* Hero Section with Featured Image */}
      <section className="py-12 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Button 
              variant="ghost" 
              className="mb-6 text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10"
              onClick={() => navigate('/blog')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Volver al Blog' : 'Back to Blog'}
            </Button>
            
            {post.category && (
              <Badge className="mb-4 bg-white/20 text-white hover:bg-white/30">
                {post.category}
              </Badge>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{post.title}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-primary-foreground/80">
              {publishedDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{publishedDate}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert Law Firm</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="container mx-auto px-4 -mt-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <article className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-muted-foreground mb-8 italic border-l-4 border-primary pl-4">
                {post.excerpt}
              </p>
            )}

            {/* Main Content */}
            <div 
              className="prose prose-lg max-w-none dark:prose-invert
                prose-headings:text-foreground prose-p:text-foreground/90
                prose-a:text-primary prose-strong:text-foreground
                prose-ul:text-foreground/90 prose-ol:text-foreground/90"
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  {post.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Share & Back */}
            <div className="mt-12 flex justify-between items-center">
              <Button variant="outline" onClick={() => navigate('/blog')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Ver más artículos' : 'More Articles'}
              </Button>
            </div>
          </div>
        </div>
      </article>

      <CTASection
        title={language === 'es' ? '¿Tiene Preguntas Legales?' : 'Have Legal Questions?'}
        description={language === 'es' 
          ? 'Nuestros abogados experimentados están aquí para brindarle orientación personalizada.'
          : 'Our experienced attorneys are here to provide personalized guidance for your specific situation.'}
      />

      <Footer />
    </div>
  );
};

export default DynamicBlogPost;